import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiPlus,
  FiMinus,
  FiArrowLeft,
  FiChevronDown,
  FiSave,
  FiCreditCard,
} from "react-icons/fi";
import { FaLiraSign } from "react-icons/fa";
import axiosInstance from "../../api/api";
import ErrorMessage from "../ErrorMessage";

const ConfirmationModal = ({ isOpen, onClose, onConfirm, message }) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl max-w-md w-full"
          >
            <p className="text-gray-700 dark:text-gray-300 mb-4">{message}</p>
            <div className="flex justify-end space-x-2">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 transition duration-150 ease-in-out text-sm"
              >
                Hayır
              </button>
              <button
                onClick={onConfirm}
                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition duration-150 ease-in-out text-sm"
              >
                Evet
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const CustomerModal = ({
  isOpen,
  onClose,
  selectedCustomer,
  packages,
  packageSearchTerm,
  setPackageSearchTerm,
  handleInputChange,
  handlePackageChange,
  handleSubmit,
}) => {
  const [activeTab, setActiveTab] = useState("info");
  const [payments, setPayments] = useState([]);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [installments, setInstallments] = useState([]);
  const [bulkInstallment, setBulkInstallment] = useState({
    installment_date: "",
    installment_count: 1,
  });
  const [showPaymentList, setShowPaymentList] = useState(true);
  const [expandedInstallments, setExpandedInstallments] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationAction, setConfirmationAction] = useState(null);
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(false);
  const [hasPaymentChanges, setHasPaymentChanges] = useState(false);
  const [hasInstallmentChanges, setHasInstallmentChanges] = useState(false);

  const fetchCustomerData = useCallback(async () => {
    if (!selectedCustomer || !selectedCustomer.id) return;
    try {
      const response = await axiosInstance.get(
        `/api/customer/${selectedCustomer.id}/`
      );
      const customerData = response.data;
      setPayments(customerData.payments || []);
    } catch (error) {
      setErrorMessage("Müşteri verilerini getirirken bir hata oluştu.");
    }
  }, [selectedCustomer]);

  useEffect(() => {
    if (isOpen) {
      setActiveTab("info");
      setShowPaymentList(true);
      setSelectedPayment(null);
      setHasPaymentChanges(false);
      setHasInstallmentChanges(false);
      setSuccessMessage("");
      setErrorMessage("");
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedCustomer && selectedCustomer.id) {
      fetchCustomerData();
    } else {
      setPayments([]);
      setInstallments([]);
    }
  }, [selectedCustomer, fetchCustomerData]);

  const handleInstallmentChange = (index, field, value) => {
    const updatedInstallments = [...installments];

    if (field === "payed_amount") {
      const maxPayableAmount = parseFloat(updatedInstallments[index].amount);

      if (parseFloat(value) > maxPayableAmount) {
        setErrorMessage("Ödenen tutar, taksit tutarını aşamaz.");
        return;
      }
    }

    if (field === "amount") {
      const newAmount = parseFloat(value);
      const currentPayedAmount = parseFloat(
        updatedInstallments[index].payed_amount
      );

      if (currentPayedAmount > newAmount) {
        setErrorMessage(
          "Ödenen tutar, yeni taksit tutarını aşamaz."
        );
        return;
      }
    }

    if (updatedInstallments[index][field] !== value) {
      updatedInstallments[index] = {
        ...updatedInstallments[index],
        [field]: value,
      };
      setInstallments(updatedInstallments);
      setHasInstallmentChanges(true);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const fetchInstallments = async (paymentId) => {
    try {
      const response = await axiosInstance.get(`/api/payment/${paymentId}/`);
      setSelectedPayment(response.data);
      setInstallments(response.data.installments || []);
    } catch (error) {
      setErrorMessage("Taksit bilgilerini getirirken bir hata oluştu.");
    }
  };

  const handlePaymentClick = async (payment) => {
    setSelectedPayment(payment);
    await fetchInstallments(payment.id);
    setShowPaymentList(false);
  };

  const handlePaymentInputChange = (e) => {
    const { name, value } = e.target;
    setSelectedPayment((prev) => {
      if (prev[name] !== value) {
        setHasPaymentChanges(true);
        return { ...prev, [name]: value };
      }
      return prev;
    });
  };

  const addInstallment = async () => {
    try {
      const newInstallment = {
        amount: 0,
        payed_amount: "",
        payment_date: new Date().toISOString().split("T")[0],
        description: "",
        payment: selectedPayment.id,
      };
      const response = await axiosInstance.post(
        "/api/installment/",
        newInstallment
      );
      setInstallments([...installments, response.data]);
      setSuccessMessage("Yeni taksit başarıyla eklendi.");
    } catch (error) {
      setErrorMessage("Taksit eklerken bir hata oluştu.");
    }
  };

  const removeInstallment = async (index) => {
    setConfirmationMessage("Bu taksiti silmek istediğinizden emin misiniz?");
    setConfirmationAction(() => async () => {
      try {
        const installmentToRemove = installments[index];
        if (installmentToRemove.id) {
          await axiosInstance.delete(
            `/api/installment/${installmentToRemove.id}/`
          );
        }
        const updatedInstallments = installments.filter((_, i) => i !== index);
        setInstallments(updatedInstallments);
        setShowConfirmation(false);
        setSuccessMessage("Taksit başarıyla silindi.");
      } catch (error) {
        setErrorMessage("Taksit silinirken bir hata oluştu.");
      }
    });
    setShowConfirmation(true);
  };

  const handleBulkInstallmentChange = (e) => {
    const { name, value } = e.target;
    setBulkInstallment((prev) => ({
      ...prev,
      [name]: name === "installment_count" ? parseInt(value, 10) : value,
    }));
  };

  const createBulkInstallments = async () => {
    try {
      await axiosInstance.post("/api/installment/bulk_create/", {
        ...bulkInstallment,
        payment: selectedPayment.id,
        installment_count: parseInt(bulkInstallment.installment_count, 10),
      });
      await fetchInstallments(selectedPayment.id);
      setSuccessMessage("Toplu taksitler başarıyla oluşturuldu.");
    } catch (error) {
      setErrorMessage("Toplu taksit oluştururken bir hata oluştu.");
    }
  };

  const savePaymentAndInstallments = async () => {
    if (isSaveButtonDisabled) return;

    setIsSaveButtonDisabled(true);
    try {
      await axiosInstance.patch(
        `/api/payment/${selectedPayment.id}/`,
        selectedPayment
      );
      for (const installment of installments) {
        if (installment.id) {
          await axiosInstance.patch(`/api/installment/${installment.id}/`, {
            amount: installment.amount,
            payed_amount: installment.payed_amount,
            payment_date: installment.payment_date,
            description: installment.description,
          });
        } else {
          await axiosInstance.post("/api/installment/", {
            ...installment,
            payment: selectedPayment.id,
          });
        }
      }
      await fetchCustomerData();
      await fetchInstallments(selectedPayment.id);
      setShowPaymentList(true);
      setSuccessMessage("Ödeme ve taksitler başarıyla kaydedildi.");
      setHasPaymentChanges(false);
      setHasInstallmentChanges(false);
    } catch (error) {
      setErrorMessage("Ödeme ve taksitler kaydedilirken bir hata oluştu.");
    }

    setTimeout(() => {
      setIsSaveButtonDisabled(false);
    }, 2000);
  };

  const addNewPayment = async () => {
    try {
      const newPayment = {
        amount: "0",
        payed_amount: "0",
        payment_type: "cash",
        discount: "0",
        description: "",
        customer: selectedCustomer.id,
      };
      const response = await axiosInstance.post("/api/payment/", newPayment);
      await fetchCustomerData();
      setSelectedPayment(response.data);
      setInstallments([]);
      setShowPaymentList(false);
      setSuccessMessage("Yeni ödeme başarıyla eklendi.");
    } catch (error) {
      setErrorMessage("Yeni ödeme eklenirken bir hata oluştu.");
    }
  };

  const deletePayment = async (paymentId) => {
    setConfirmationMessage("Bu ödemeyi silmek istediğinizden emin misiniz?");
    setConfirmationAction(() => async () => {
      try {
        await axiosInstance.delete(`/api/payment/${paymentId}/`);
        await fetchCustomerData();
        setSelectedPayment(null);
        setInstallments([]);
        setShowPaymentList(true);
        setShowConfirmation(false);
        setSuccessMessage("Ödeme başarıyla silindi.");
      } catch (error) {
        setErrorMessage("Ödeme silinirken bir hata oluştu.");
      }
    });
    setShowConfirmation(true);
  };

  const toggleInstallmentAccordion = () => {
    setExpandedInstallments(!expandedInstallments);
  };

  if (!isOpen || !selectedCustomer) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm overflow-y-auto h-full w-full flex items-center justify-center p-4 z-50"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white dark:bg-gray-800 p-6 md:p-8 rounded-xl shadow-2xl w-full max-w-7xl mx-auto max-h-[90vh] overflow-y-auto"
          >
            {/* Header */}
            <div className="flex justify-between items-center mb-6 border-b border-gray-200 dark:border-gray-700 pb-4">
              <h3 className="text-2xl font-bold text-blue-600 dark:text-blue-400 flex items-center">
                {selectedCustomer && selectedCustomer.id ? (
                  <>
                    <span>Müşteri Düzenle:</span>
                    <span className="ml-2 text-gray-700 dark:text-gray-300">
                      {selectedCustomer.name}
                    </span>
                  </>
                ) : (
                  "Yeni Müşteri Ekle"
                )}
              </h3>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
              >
                <svg
                  className="w-6 h-6 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* tablar */}
            <div className="mb-6">
              <div className="flex space-x-1 bg-gray-100 dark:bg-gray-700 p-1 rounded-lg">
                {["info", "packages", "payments"].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`flex-1 py-2 px-4 rounded-md transition-all duration-200 ${
                      activeTab === tab
                        ? "bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm"
                        : "text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
                    }`}
                  >
                    {tab === "info"
                      ? "Müşteri Bilgileri"
                      : tab === "packages"
                      ? "Paketler"
                      : "Ödemeler ve Taksitler"}
                  </button>
                ))}
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {activeTab === "info" && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      İsim
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={selectedCustomer.name || ""}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 bg-white dark:bg-gray-700 rounded-md text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 focus:border-blue-300 dark:focus:border-blue-500 focus:ring"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      TC Kimlik Numarası
                    </label>
                    <input
                      type="number"
                      name="identify_number"
                      value={selectedCustomer.identify_number || ""}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 bg-white dark:bg-gray-700 rounded-md text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 focus:border-blue-300 dark:focus:border-blue-500 focus:ring"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Telefon
                    </label>
                    <div className="flex">
                      <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-sm">
                        +90
                      </span>
                      <input
                        type="tel"
                        name="phone"
                        value={(selectedCustomer.phone || "").replace(
                          /^\+90/,
                          ""
                        )}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 bg-white dark:bg-gray-700 rounded-r-md text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 focus:border-blue-300 dark:focus:border-blue-500 focus:ring"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      E-posta
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={selectedCustomer.email || ""}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 bg-white dark:bg-gray-700 rounded-md text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 focus:border-blue-300 dark:focus:border-blue-500 focus:ring"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Not
                    </label>
                    <textarea
                      name="note"
                      value={selectedCustomer.note || ""}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 bg-white dark:bg-gray-700 rounded-md text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 focus:border-blue-300 dark:focus:border-blue-500 focus:ring"
                      rows="3"
                    ></textarea>
                  </div>
                </div>
              )}

              {activeTab === "packages" && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Paketler
                    </label>
                    <div className="mb-2">
                      <input
                        type="text"
                        placeholder="Paket ara..."
                        value={packageSearchTerm}
                        onChange={(e) => setPackageSearchTerm(e.target.value)}
                        className="w-full px-3 py-2 bg-white dark:bg-gray-700 rounded-md text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 focus:border-blue-300 dark:focus:border-blue-500 focus:ring"
                      />
                    </div>
                    <div className="space-y-3 max-h-96 p-3 overflow-y-auto pr-2 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                      {packages
                        .filter((pkg) =>
                          pkg.name
                            .toLowerCase()
                            .includes(packageSearchTerm.toLowerCase())
                        )
                        .reverse()
                        .map((pkg) => (
                          <label
                            key={pkg.id}
                            className="flex items-center space-x-3"
                          >
                            <input
                              type="checkbox"
                              value={pkg.id}
                              checked={(
                                selectedCustomer.company_packages || []
                              ).includes(pkg.id)}
                              onChange={handlePackageChange}
                              className="form-checkbox h-5 w-5 text-blue-600 dark:text-blue-400 rounded focus:ring-blue-400 dark:focus:ring-blue-600"
                            />
                            <span className="text-gray-700 dark:text-gray-300">
                              {pkg.name}
                              <span className="inline-flex items-center ml-2 px-1 py-0.5 text-xs font-medium text-blue-500 bg-blue-100 rounded-full dark:text-blue-300 dark:bg-blue-700">
                                Seans: {pkg.seance_count}
                              </span>
                            </span>
                          </label>
                        ))}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "payments" && (
                <div className="space-y-6">
                  {showPaymentList ? (
                    <>
                      <div className="flex justify-between items-center mb-4">
                        <h4 className="text-lg font-semibold">Ödemeler</h4>
                        <button
                          type="button"
                          onClick={addNewPayment}
                          className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition duration-150 ease-in-out text-sm flex items-center"
                        >
                          <FiPlus className="mr-2" /> Yeni Ödeme Ekle
                        </button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {payments.map((payment) => (
                          <div
                            key={payment.id}
                            className="bg-gray-50 dark:bg-gray-700 shadow-sm rounded-lg overflow-hidden cursor-pointer hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
                            onClick={() => handlePaymentClick(payment)}
                          >
                            <div className="p-4">
                              <div className="flex justify-between items-center mb-3">
                                <h5 className="font-semibold text-lg text-gray-800 dark:text-gray-200">
                                  Ödeme #{payment.id}
                                </h5>
                                <span className="text-sm text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-600 px-2 py-1 rounded-full">
                                  {new Date(
                                    payment.payment_date
                                  ).toLocaleDateString()}
                                </span>
                              </div>
                              <div className="space-y-2">
                                <div className="flex justify-between items-center">
                                  <span className="text-gray-600 dark:text-gray-300 flex items-center">
                                    <FaLiraSign className="mr-1" /> Tutar:
                                  </span>
                                  <span className="font-medium text-gray-800 dark:text-gray-200">
                                    {payment.amount} TL
                                  </span>
                                </div>
                                <div className="flex justify-between items-center">
                                  <span className="text-gray-600 dark:text-gray-300 flex items-center">
                                    <FiCreditCard className="mr-1" /> Ödenen:
                                  </span>
                                  <span className="font-medium text-green-600 dark:text-green-400">
                                    {payment.payed_amount} TL
                                  </span>
                                </div>
                              </div>
                              <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                                <div className="flex justify-between items-center">
                                  <span className="text-sm text-gray-500 dark:text-gray-400">
                                    Kalan:
                                  </span>
                                  <span className="font-medium text-blue-600 dark:text-blue-400">
                                    {(
                                      payment.amount - payment.payed_amount
                                    ).toFixed(2)}{" "}
                                    TL
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="space-y-6">
                      <div className="flex items-center mb-4">
                        <button
                          type="button"
                          onClick={() => setShowPaymentList(true)}
                          className="mr-4 text-blue-500 hover:text-blue-700 flex items-center"
                        >
                          <FiArrowLeft size={20} className="mr-1" />
                          <span>Geri Dön</span>
                        </button>
                        <h4 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
                          Ödeme ve Taksit Detayları
                        </h4>
                      </div>

                      {/* ödeme kartları*/}
                      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                          <h5 className="text-lg font-semibold text-gray-800 dark:text-gray-200 flex items-center">
                            <FiCreditCard className="mr-2" />
                            Ödeme Bilgileri
                          </h5>
                        </div>
                        <div className="p-4 space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Tutar
                              </label>
                              <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                  <FaLiraSign className="text-gray-400" />
                                </div>
                                <input
                                  type="number"
                                  name="amount"
                                  value={selectedPayment.amount}
                                  onChange={handlePaymentInputChange}
                                  className="w-full pl-10 px-3 py-2.5 bg-gray-50 dark:bg-gray-700 rounded-lg text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 focus:border-blue-400 dark:focus:border-blue-500 focus:ring focus:ring-blue-200 dark:focus:ring-blue-500/20 transition-all duration-200"
                                />
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Ödenen Tutar
                              </label>
                              <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                  <FiCreditCard className="text-gray-400" />
                                </div>
                                <input
                                  type="number"
                                  name="payed_amount"
                                  value={selectedPayment.payed_amount}
                                  onChange={handlePaymentInputChange}
                                  className="w-full pl-10 px-3 py-2.5 bg-gray-50 dark:bg-gray-700 rounded-lg text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 focus:border-blue-400 dark:focus:border-blue-500 focus:ring focus:ring-blue-200 dark:focus:ring-blue-500/20 transition-all duration-200"
                                />
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Ödeme Tipi
                              </label>
                              <select
                                name="payment_type"
                                value={selectedPayment.payment_type}
                                onChange={handlePaymentInputChange}
                                className="w-full px-3 py-2.5 bg-gray-50 dark:bg-gray-700 rounded-lg text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 focus:border-blue-400 dark:focus:border-blue-500 focus:ring focus:ring-blue-200 dark:focus:ring-blue-500/20 transition-all duration-200"
                              >
                                <option value="cash">Nakit</option>
                                <option value="card">Kart</option>
                                <option value="eft">Havale/EFT</option>
                                <option value="other">Diğer</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                İndirim
                              </label>
                              <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                  <FaLiraSign className="text-gray-400" />
                                </div>
                                <input
                                  type="number"
                                  name="discount"
                                  value={selectedPayment.discount}
                                  onChange={handlePaymentInputChange}
                                  className="w-full pl-10 px-3 py-2.5 bg-gray-50 dark:bg-gray-700 rounded-lg text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 focus:border-blue-400 dark:focus:border-blue-500 focus:ring focus:ring-blue-200 dark:focus:ring-blue-500/20 transition-all duration-200"
                                />
                              </div>
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Açıklama
                            </label>
                            <textarea
                              name="description"
                              value={selectedPayment.description}
                              onChange={handlePaymentInputChange}
                              className="w-full px-3 py-2.5 bg-gray-50 dark:bg-gray-700 rounded-lg text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 focus:border-blue-400 dark:focus:border-blue-500 focus:ring focus:ring-blue-200 dark:focus:ring-blue-500/20 transition-all duration-200"
                              rows="2"
                            ></textarea>
                          </div>
                        </div>
                        <div className="p-4 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center">
                          <button
                            type="button"
                            onClick={savePaymentAndInstallments}
                            disabled={isSaveButtonDisabled}
                            className={`px-4 py-2 ${
                              isSaveButtonDisabled
                                ? "bg-gray-400 cursor-not-allowed"
                                : "bg-green-500 hover:bg-green-600"
                            } text-white rounded-lg shadow-sm transition duration-150 ease-in-out text-sm flex items-center ${
                              hasPaymentChanges ? "ring-4 ring-green-400/50 dark:ring-green-500/50 animate-pulse" : ""
                            }`}
                          >
                            <FiSave className="mr-2" />
                            {isSaveButtonDisabled ? "Kaydediliyor..." : "Ödemeyi Kaydet"}
                          </button>
                          <button
                            type="button"
                            onClick={() => deletePayment(selectedPayment.id)}
                            className="px-4 py-2 bg-red-500 text-white rounded-lg shadow-sm hover:bg-red-600 transition duration-150 ease-in-out text-sm flex items-center"
                          >
                            <FiMinus className="mr-2" /> Ödemeyi Sil
                          </button>
                        </div>
                      </div>

                      {/* taksit kısımı*/}
                      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                          <button
                            type="button"
                            onClick={toggleInstallmentAccordion}
                            className="w-full flex justify-between items-center"
                          >
                            <h5 className="text-lg font-semibold text-gray-800 dark:text-gray-200 flex items-center">
                              <FiCreditCard className="mr-2" />
                              Taksit Bilgileri
                            </h5>
                            <motion.div
                              animate={{ rotate: expandedInstallments ? 180 : 0 }}
                              transition={{ duration: 0.2 }}
                            >
                              <FiChevronDown className="text-gray-500" />
                            </motion.div>
                          </button>
                        </div>

                        <AnimatePresence>
                          {expandedInstallments && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.3 }}
                              className="overflow-hidden"
                            >
                              <div className="p-4 space-y-6">
                                {/* Taksit Özeti */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                                    <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Toplam Taksit</div>
                                    <div className="text-xl font-semibold text-gray-800 dark:text-gray-200">
                                      {installments.length} Adet
                                    </div>
                                  </div>
                                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                                    <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Toplam Tutar</div>
                                    <div className="text-xl font-semibold text-gray-800 dark:text-gray-200">
                                      {installments.reduce((sum, inst) => sum + parseFloat(inst.amount || 0), 0).toFixed(2)} ₺
                                    </div>
                                  </div>
                                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                                    <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Ödenen Tutar</div>
                                    <div className="text-xl font-semibold text-green-600 dark:text-green-400">
                                      {installments.reduce((sum, inst) => sum + parseFloat(inst.payed_amount || 0), 0).toFixed(2)} ₺
                                    </div>
                                  </div>
                                </div>

                                {/* taksit mapi listesi */}
                                <div className="space-y-4">
                                  {installments.map((installment, index) => (
                                    <div
                                      key={index}
                                      className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-3"
                                    >
                                      <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 px-2 py-1 rounded-full">
                                          Taksit #{index + 1}
                                        </span>
                                        <button
                                          type="button"
                                          onClick={() => removeInstallment(index)}
                                          className="p-1.5 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500 rounded-full transition-colors"
                                        >
                                          <FiMinus />
                                        </button>
                                      </div>
                                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                        <div>
                                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                                            Ödeme Tarihi
                                          </label>
                                          <input
                                            type="date"
                                            value={formatDate(installment.payment_date)}
                                            onChange={(e) =>
                                              handleInstallmentChange(
                                                index,
                                                "payment_date",
                                                e.target.value
                                              )
                                            }
                                            className="w-full px-3 py-2 bg-white dark:bg-gray-600 rounded-lg text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-500 focus:border-blue-400 dark:focus:border-blue-500 focus:ring focus:ring-blue-200 dark:focus:ring-blue-500/20 transition-all duration-200"
                                          />
                                        </div>
                                        <div>
                                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                                            Taksit Tutarı
                                          </label>
                                          <input
                                            type="number"
                                            value={installment.amount}
                                            onChange={(e) =>
                                              handleInstallmentChange(
                                                index,
                                                "amount",
                                                e.target.value
                                              )
                                            }
                                            className="w-full px-3 py-2 bg-white dark:bg-gray-600 rounded-lg text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-500 focus:border-blue-400 dark:focus:border-blue-500 focus:ring focus:ring-blue-200 dark:focus:ring-blue-500/20 transition-all duration-200"
                                          />
                                        </div>
                                        <div>
                                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                                            Ödenen Tutar
                                          </label>
                                          <input
                                            type="number"
                                            value={installment.payed_amount}
                                            onChange={(e) =>
                                              handleInstallmentChange(
                                                index,
                                                "payed_amount",
                                                e.target.value
                                              )
                                            }
                                            className={`w-full px-3 py-2 rounded-lg border focus:ring transition-all duration-200 ${
                                              parseFloat(installment.amount) ===
                                              parseFloat(installment.payed_amount)
                                                ? "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800 focus:border-green-400 focus:ring-green-200"
                                                : "bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-500 focus:border-blue-400 focus:ring-blue-200"
                                            }`}
                                          />
                                        </div>
                                        <div>
                                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                                            Açıklama
                                          </label>
                                          <input
                                            type="text"
                                            value={installment.description}
                                            onChange={(e) =>
                                              handleInstallmentChange(
                                                index,
                                                "description",
                                                e.target.value
                                              )
                                            }
                                            className="w-full px-3 py-2 bg-white dark:bg-gray-600 rounded-lg text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-500 focus:border-blue-400 dark:focus:border-blue-500 focus:ring focus:ring-blue-200 dark:focus:ring-blue-500/20 transition-all duration-200"
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>

                                {/* taksit işlemi */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div className="bg-white dark:bg-gray-700 p-4 rounded-lg shadow-sm">
                                    <h6 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                                      Tek Taksit Ekle
                                    </h6>
                                    <button
                                      type="button"
                                      onClick={addInstallment}
                                      className="w-full px-4 py-2.5 bg-blue-500 hover:bg-blue-600 text-white rounded-lg shadow-sm transition-colors flex items-center justify-center"
                                    >
                                      <FiPlus className="mr-2" /> Yeni Taksit
                                    </button>
                                  </div>

                                  <div className="bg-white dark:bg-gray-700 p-4 rounded-lg shadow-sm">
                                    <h6 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                                      Toplu Taksit Oluştur
                                    </h6>
                                    <div className="space-y-3">
                                      <div className="grid grid-cols-2 gap-2">
                                        <input
                                          type="date"
                                          name="installment_date"
                                          value={bulkInstallment.installment_date}
                                          onChange={handleBulkInstallmentChange}
                                          className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-600 rounded-lg text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-500 focus:border-blue-400 dark:focus:border-blue-500 focus:ring focus:ring-blue-200 dark:focus:ring-blue-500/20 transition-all duration-200"
                                        />
                                        <input
                                          type="number"
                                          name="installment_count"
                                          value={bulkInstallment.installment_count}
                                          onChange={handleBulkInstallmentChange}
                                          min="1"
                                          placeholder="Taksit Sayısı"
                                          className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-600 rounded-lg text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-500 focus:border-blue-400 dark:focus:border-blue-500 focus:ring focus:ring-blue-200 dark:focus:ring-blue-500/20 transition-all duration-200"
                                        />
                                      </div>
                                      <button
                                        type="button"
                                        onClick={createBulkInstallments}
                                        className="w-full px-4 py-2.5 bg-blue-500 hover:bg-blue-600 text-white rounded-lg shadow-sm transition-colors flex items-center justify-center"
                                      >
                                        <FiPlus className="mr-2" /> Toplu Taksit Oluştur
                                      </button>
                                    </div>
                                  </div>
                                </div>

                                {/* değişiklik kaydet tüm*/}
                                <div className="mt-6">
                                  <button
                                    type="button"
                                    onClick={savePaymentAndInstallments}
                                    disabled={isSaveButtonDisabled}
                                    className={`w-full px-4 py-3 rounded-lg shadow-sm transition-colors flex items-center justify-center ${
                                      isSaveButtonDisabled
                                        ? "bg-gray-400 cursor-not-allowed"
                                        : "bg-green-500 hover:bg-green-600"
                                    } text-white font-medium ${
                                      hasInstallmentChanges ? "ring-4 ring-green-400/50 dark:ring-green-500/50 animate-pulse" : ""
                                    }`}
                                  >
                                    <FiSave className="mr-2" />
                                    {isSaveButtonDisabled ? "Kaydediliyor..." : "Tüm Değişiklikleri Kaydet"}
                                  </button>
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end space-x-3 mt-8">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-400 dark:focus:ring-gray-500 transition duration-150 ease-in-out text-sm"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 dark:bg-blue-600 text-white rounded-md hover:bg-blue-600 dark:hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 dark:focus:ring-blue-500 transition duration-150 ease-in-out text-sm"
                >
                  Kaydet
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
      <ErrorMessage
        message={errorMessage}
        onClose={() => setErrorMessage("")}
      />
      <ErrorMessage
        message={successMessage}
        type="success"
        onClose={() => setSuccessMessage("")}
      />
      <ConfirmationModal
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={confirmationAction}
        message={confirmationMessage}
      />
    </AnimatePresence>
  );
};

export default CustomerModal;